// src/components/RolesManager.js — admin UI to create roles and grant permissions.
import React, { useState, useEffect, useCallback } from 'react';
import { getRoles, getPermissionCatalog, createRole, updateRole, deleteRole } from '../utils/api';
import { Modal } from './UI';

const EMPTY_ROLE = { name: '', description: '', leads_scope: 'all', permissions: [] };

export default function RolesManager({ toast }) {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);   // [{ module, items:[{perm_key,label,description}] }]
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // role object being created/edited (null = closed)
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getRoles(), getPermissionCatalog()])
      .then(([r, c]) => { setRoles(r.data); setCatalog(c.data); })
      .catch(() => toast('Failed to load roles'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...EMPTY_ROLE });
  const openEdit = (role) => setEditing({
    id: role.id,
    name: role.name,
    description: role.description || '',
    leads_scope: role.leads_scope || 'all',
    permissions: [...(role.permissions || [])],
    is_system: role.is_system,
    is_super_admin: role.is_super_admin,
  });

  const allPermKeys = catalog.flatMap(g => g.items.map(i => i.perm_key));

  const togglePerm = (key) => setEditing(e => ({
    ...e,
    permissions: e.permissions.includes(key)
      ? e.permissions.filter(k => k !== key)
      : [...e.permissions, key],
  }));

  const toggleModule = (items) => setEditing(e => {
    const keys = items.map(i => i.perm_key);
    const allOn = keys.every(k => e.permissions.includes(k));
    return {
      ...e,
      permissions: allOn
        ? e.permissions.filter(k => !keys.includes(k))
        : [...new Set([...e.permissions, ...keys])],
    };
  });

  const save = async () => {
    if (!editing.name.trim()) { toast('Role name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description,
        leads_scope: editing.leads_scope,
        permissions: editing.permissions,
      };
      if (editing.id) await updateRole(editing.id, payload);
      else await createRole(payload);
      toast(editing.id ? 'Role updated!' : 'Role created!');
      setEditing(null);
      load();
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to save role');
    } finally { setSaving(false); }
  };

  const remove = async (role) => {
    try {
      await deleteRole(role.id);
      toast('Role deleted');
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to delete role');
      setConfirmDelete(null);
    }
  };

  const isSuper = editing?.is_super_admin === 1 || editing?.is_super_admin === true;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div className="settings-section-title" style={{ margin: 0 }}>Roles &amp; Permissions</div>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={openNew}>
          <i className="ti ti-plus" /> New Role
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 0, marginBottom: 12 }}>
        Create roles and choose exactly what each can see and do. Assign roles to team members above.
      </p>

      {loading ? (
        <div style={{ color: 'var(--gray-400)', fontSize: 13, padding: 8 }}>Loading roles...</div>
      ) : roles.map(role => (
        <div key={role.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--gray-100)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {role.name}
              {!!role.is_super_admin && <span className="status-badge s-hot" style={{ fontSize: 10 }}>Full access</span>}
              {!!role.is_system && <span className="status-badge s-new" style={{ fontSize: 10 }}>System</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
              {role.description || 'No description'}
              {' · '}{role.is_super_admin ? 'All permissions' : `${role.permissions.length} permission(s)`}
              {' · '}Leads: {role.leads_scope === 'own' ? 'Own only' : 'All'}
              {' · '}{role.user_count} user(s)
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => openEdit(role)} title="Edit role" style={{ padding: '4px 8px' }}>
            <i className="ti ti-edit" />
          </button>
          {!role.is_system && (
            <button className="btn btn-outline btn-sm" onClick={() => setConfirmDelete(role)} title="Delete role" style={{ padding: '4px 8px', color: 'var(--red)' }}>
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ))}

      {/* ── Create / Edit Role Modal ── */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? `Edit Role — ${editing.name}` : 'New Role'} width={620}>
        {editing && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Role Name</label>
                <input className="form-input" value={editing.name} onChange={e => setEditing(r => ({ ...r, name: e.target.value }))} placeholder="e.g. Sales Rep" />
              </div>
              <div className="form-group">
                <label className="form-label">Leads Visibility</label>
                <select className="form-input" value={editing.leads_scope} disabled={isSuper}
                  onChange={e => setEditing(r => ({ ...r, leads_scope: e.target.value }))}>
                  <option value="all">All leads</option>
                  <option value="own">Only leads assigned to them</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" value={editing.description} onChange={e => setEditing(r => ({ ...r, description: e.target.value }))} placeholder="What this role is for" />
            </div>

            {isSuper ? (
              <div style={{ background: 'var(--gray-50)', border: '0.5px solid var(--gray-200)', borderRadius: 8, padding: 14, fontSize: 13, color: 'var(--gray-600)' }}>
                <i className="ti ti-shield-check" /> This is a full-access administrator role. It always holds every permission (including new ones) and can't be locked out.
              </div>
            ) : (
              <div>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Permissions</label>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '0.5px solid var(--gray-200)', borderRadius: 8 }}>
                  {catalog.map(group => {
                    const keys = group.items.map(i => i.perm_key);
                    const allOn = keys.every(k => editing.permissions.includes(k));
                    const someOn = keys.some(k => editing.permissions.includes(k));
                    return (
                      <div key={group.module} style={{ borderBottom: '0.5px solid var(--gray-100)', padding: '10px 12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12, color: 'var(--black)', marginBottom: 6 }}>
                          <input type="checkbox" checked={allOn} ref={el => { if (el) el.indeterminate = someOn && !allOn; }} onChange={() => toggleModule(group.items)} />
                          {group.module}
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', paddingLeft: 24 }}>
                          {group.items.map(item => (
                            <label key={item.perm_key} title={item.description} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--gray-600)' }}>
                              <input type="checkbox" checked={editing.permissions.includes(item.perm_key)} onChange={() => togglePerm(item.perm_key)} />
                              {item.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <button type="button" className="btn-link" style={linkBtn} onClick={() => setEditing(r => ({ ...r, permissions: [...allPermKeys] }))}>Select all</button>
                  <button type="button" className="btn-link" style={linkBtn} onClick={() => setEditing(r => ({ ...r, permissions: [] }))}>Clear all</button>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : (editing.id ? 'Save Role' : 'Create Role')}</button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Role" width={400}>
        {confirmDelete && (
          <>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 20 }}>
              Delete the <strong>{confirmDelete.name}</strong> role? This can't be undone.
              {confirmDelete.user_count > 0 && ` You must reassign its ${confirmDelete.user_count} user(s) first.`}
            </p>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => remove(confirmDelete)}>Delete Role</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'var(--font)' };
